import Head from 'next/head';
import 'intersection-observer';
import {Provider} from 'jotai';
import Script from 'next/script';
import type {AppProps} from 'next/app';
import React, {useEffect, useState} from 'react';
import 'focus-visible/dist/focus-visible.min.js';
import {NextIntlClientProvider} from 'next-intl';
import {Inter, Plus_Jakarta_Sans} from 'next/font/google';
import {MotionConfig, type Transition} from 'motion/react';
import type {NextPageContext, NextComponentType} from 'next';
import {appWithTranslation, useTranslation} from 'next-i18next';
import {QueryClient, QueryClientProvider, HydrationBoundary} from '@tanstack/react-query';
import useScrollRestoration from 'src/utils/useScrollRestoration';
import HighlightProvider from 'src/components/HighlightProvider';
import LanguageHeadTags from 'src/components/LanguageHeadTags';
import nextI18NextConfig from '../next-i18next.config.js';
import initPolyfills from 'src/utils/initPolyfills';
import History from 'src/components/History';

const inter = Inter({subsets: ['latin-ext']});
const jakarta = Plus_Jakarta_Sans({subsets: ['latin-ext']});

import 'src/styles.css';

const defaultTransition: Transition = {
  ease: [0.25, 1, 0.5, 1]
};

const queryClientOptions = {
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 1000 * 5
    }
  }
};

type PanelistaAppProps = AppProps & {
  Component: NextComponentType<NextPageContext, any, {}> & {getLayout: Function; isPublicPage?: boolean};
  err?: any;
};

const PanelistaApp = (props: PanelistaAppProps) => {
  const {Component, pageProps, err, router} = props;

  const {t, i18n} = useTranslation();

  const language = pageProps?.language || i18n.language;

  const [queryClient] = useState(() => new QueryClient(queryClientOptions));

  useScrollRestoration(router);

  const getLayout = Component.getLayout || ((page) => page);

  useEffect(() => {
    initPolyfills();
  }, []);

  const isProduction = process.env.NEXT_PUBLIC_APP_STAGE === 'production';

  return (
    <>
      <style jsx global>{`
        :root {
          --font-inter: ${inter.style.fontFamily};
          --font-jakarta: ${jakarta.style.fontFamily};
        }
      `}</style>

      <Head>
        {pageProps?.isPrivate || !isProduction ? <meta name="robots" content="noindex" key="robots" /> : null}

        <title>Panelista</title>
        <meta
          key="og:url"
          property="og:url"
          content={process.env.NEXT_PUBLIC_PRODUCTION_APP_URL + '/' + language + router.asPath}
        />
        <meta key="og:title" content={t('common.landing page title', {lng: language})} property="og:title" />
        <meta name="description" content={t('common.meta description', {lng: language})} key="description" />
        <meta key="og:description" content={t('common.meta description', {lng: language})} property="og:description" />
        <meta
          key="og:image"
          property="og:image"
          content={`https://panelista.s3.nl-ams.scw.cloud/website/social_preview_${
            language === 'sv' ? 'sv' : 'en'
          }.jpg`}
        />

        {pageProps?.isPrivate ? null : <LanguageHeadTags router={router} />}
      </Head>

      {isProduction ? (
        <Script
          defer
          data-domain={
            Component.isPublicPage ? 'panelista.com,rollup.panelista-articles.panelista.com' : 'product.panelista.com'
          }
          src="https://plausible.io/js/plausible.js"
        />
      ) : null}

      {/* Script for enabling custom events */}
      <Script id="plausible-inline">{`window.plausible = window.plausible || function() { (window.plausible.q = window.plausible.q || []).push(arguments) }`}</Script>

      <QueryClientProvider client={queryClient}>
        <HydrationBoundary state={pageProps.dehydratedState}>
          <Provider>
            <NextIntlClientProvider timeZone="Europe/Stockholm" locale={language} messages={pageProps.messages}>
              <MotionConfig transition={defaultTransition}>
                <HighlightProvider />

                {getLayout(<Component err={err} {...pageProps} />)}
              </MotionConfig>
            </NextIntlClientProvider>
            <History />
          </Provider>
        </HydrationBoundary>
      </QueryClientProvider>
    </>
  );
};

// @ts-ignore
export default appWithTranslation(PanelistaApp, nextI18NextConfig);
